import * as THREE from 'three';
import { exportWithStatus } from '../hosts/exportFile';
import { parseKeyframeProject, sampleTimeline, timelineDuration, } from './keyframes';
import { filmState } from '../state/film.svelte';
import { viewerState } from '../state/viewer.svelte';
import { getBackgroundCssColor } from '../sceneBrightness';
const isVSCode = typeof acquireVsCodeApi !== 'undefined';
/**
 * Video mode: an ordered list of camera keyframes played back as a smooth
 * camera path (film/keyframes.ts), optionally recorded from the canvas with
 * MediaRecorder, and serializable to JSON so a camera path can be reproduced
 * later. The host keeps rendering through its normal on-demand loop; playback
 * just moves the camera and requests renders, so it works identically in the
 * VS Code webview and the standalone page.
 */
export class FilmManager {
    constructor(host) {
        this.keyframes = [];
        this.keyframeCounter = 0;
        this.playing = false;
        this.rafId = null;
        this.playStart = 0;
        this.savedPose = null;
        this.recorder = null;
        this.recordedChunks = [];
        this.recordedMime = '';
        this.recordingFrameId = null;
        this.recordingCanvas = null;
        // The canvas clears with alpha 0 and the visible background is CSS on the
        // canvas element, so captureStream would record transparency (black in the
        // video). During recording the background is rendered into the scene
        // instead; this holds what to restore afterwards.
        this.savedBackground = null;
        this.backgroundSwapped = false;
        this.frustumGroup = null;
        /**
         * A navigation gesture takes over from the animated camera at its exact
         * current pose. The event then continues to the re-enabled controls, so the
         * same pointerdown/wheel/touchstart begins the user's movement immediately.
         */
        this.onNavigationIntent = (event) => {
            if (!this.playing || !this.host.renderer.domElement.contains(event.target)) {
                return;
            }
            this.stop(false);
            this.host.showStatus('Camera path paused for manual navigation');
        };
        this.tick = () => {
            if (!this.playing) {
                return;
            }
            const loop = filmState.loop;
            const total = timelineDuration(this.keyframes, loop);
            let t = (performance.now() - this.playStart) / 1000;
            if (t >= total) {
                if (loop && !this.recorder) {
                    // The looping timeline ends exactly where it starts (closing segment
                    // flies back to the first keyframe), so wrapping t is seamless.
                    const cycles = Math.floor(t / total);
                    this.playStart += cycles * total * 1000;
                    t -= cycles * total;
                }
                else {
                    // Show the final pose for one frame, then restore. A recording with
                    // loop enabled runs the loop exactly once and ends back at the start.
                    this.applySample(sampleTimeline(this.keyframes, total, loop));
                    this.stop();
                    return;
                }
            }
            this.applySample(sampleTimeline(this.keyframes, t, loop));
            this.rafId = requestAnimationFrame(this.tick);
        };
        this.host = host;
        // Listen on window capture so playback yields before any mouse, wheel, or
        // touch controller sees the gesture. In particular TouchNavigation records
        // the controls' enabled state on pointerdown; cancelling here prevents it
        // from restoring the playback-disabled state when the finger is lifted.
        window.addEventListener('pointerdown', this.onNavigationIntent, { capture: true });
        window.addEventListener('wheel', this.onNavigationIntent, { capture: true, passive: true });
        window.addEventListener('touchstart', this.onNavigationIntent, {
            capture: true,
            passive: true,
        });
    }
    getKeyframes() {
        return this.keyframes;
    }
    isPlaying() {
        return this.playing;
    }
    addKeyframeFromCamera() {
        const c = this.host.camera;
        this.keyframeCounter++;
        this.keyframes.push({
            name: `Keyframe ${this.keyframeCounter}`,
            position: c.position.toArray(),
            target: this.host.controls.target.toArray(),
            quaternion: c.quaternion.toArray(),
            fov: c.fov,
            duration: 2,
            dwell: 0,
        });
        this.syncState();
        this.host.showStatus(`Added keyframe ${this.keyframes.length}`);
    }
    removeKeyframe(index) {
        if (index < 0 || index >= this.keyframes.length) {
            return;
        }
        this.keyframes.splice(index, 1);
        this.syncState();
    }
    moveKeyframe(index, delta) {
        const to = index + delta;
        if (index < 0 || index >= this.keyframes.length || to < 0 || to >= this.keyframes.length) {
            return;
        }
        const [key] = this.keyframes.splice(index, 1);
        this.keyframes.splice(to, 0, key);
        this.syncState();
    }
    updateKeyframe(index, patch) {
        const key = this.keyframes[index];
        if (!key) {
            return;
        }
        if (patch.duration !== undefined && Number.isFinite(patch.duration) && patch.duration > 0) {
            key.duration = patch.duration;
        }
        if (patch.dwell !== undefined && Number.isFinite(patch.dwell) && patch.dwell >= 0) {
            key.dwell = patch.dwell;
        }
        this.syncState();
    }
    /** Re-capture the current camera into an existing keyframe. */
    overwriteKeyframe(index) {
        const key = this.keyframes[index];
        if (!key) {
            return;
        }
        const c = this.host.camera;
        key.position = c.position.toArray();
        key.target = this.host.controls.target.toArray();
        key.quaternion = c.quaternion.toArray();
        key.fov = c.fov;
        this.syncState();
        this.host.showStatus(`Updated ${key.name} to current view`);
    }
    /** Jump the interactive camera to a keyframe (for editing/inspection). */
    goToKeyframe(index) {
        const key = this.keyframes[index];
        if (!key || this.playing) {
            return;
        }
        this.applySample({
            position: new THREE.Vector3().fromArray(key.position),
            target: new THREE.Vector3().fromArray(key.target),
            up: new THREE.Vector3(0, 1, 0).applyQuaternion(new THREE.Quaternion().fromArray(key.quaternion)),
            fov: key.fov,
        });
    }
    play() {
        if (this.playing) {
            return;
        }
        if (this.keyframes.length < 2) {
            this.host.showStatus('Video mode needs at least 2 keyframes to play');
            return;
        }
        const c = this.host.camera;
        this.savedPose = {
            position: c.position.clone(),
            target: this.host.controls.target.clone(),
            up: c.up.clone(),
            fov: c.fov,
        };
        this.host.controls.enabled = false;
        // Kill any leftover trackball fling momentum so it can't fight playback.
        this.host.controls._lastAngle = 0;
        this.playing = true;
        this.playStart = performance.now();
        filmState.playing = true;
        this.tick();
    }
    /** Stop playback, normally restoring the pre-playback camera. */
    stop(restorePose = true) {
        if (!this.playing) {
            return;
        }
        this.playing = false;
        filmState.playing = false;
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        this.host.controls.enabled = true;
        if (restorePose && this.savedPose) {
            this.applySample(this.savedPose);
        }
        this.savedPose = null;
        if (this.recorder) {
            this.finishRecording();
        }
    }
    applySample(sample) {
        const c = this.host.camera;
        c.position.copy(sample.position);
        this.host.controls.target.copy(sample.target);
        c.up.copy(sample.up);
        c.lookAt(sample.target);
        if (c.fov !== sample.fov) {
            c.fov = sample.fov;
            viewerState.cameraFov = sample.fov;
            c.updateProjectionMatrix();
        }
        this.host.requestRender();
    }
    // ---------------------------------------------------------------- recording
    updateRecordingSettings(settings) {
        if (this.recorder) {
            return;
        }
        if (settings.resolution && ['viewport', '720p', '1080p', '4k'].includes(settings.resolution)) {
            filmState.recordingResolution = settings.resolution;
        }
        if (settings.fps === 30 || settings.fps === 60) {
            filmState.recordingFps = settings.fps;
        }
        if (settings.bitrate && [6000000, 12000000, 20000000].includes(settings.bitrate)) {
            filmState.recordingBitrate = settings.bitrate;
        }
    }
    createRecordingStream() {
        const source = this.host.renderer.domElement;
        const fps = filmState.recordingFps;
        if (filmState.recordingResolution === 'viewport') {
            return source.captureStream(fps);
        }
        const [width, height] = filmState.recordingResolution === '4k'
            ? [3840, 2160]
            : filmState.recordingResolution === '1080p'
                ? [1920, 1080]
                : [1280, 720];
        const output = document.createElement('canvas');
        output.width = width;
        output.height = height;
        const context = output.getContext('2d');
        if (!context) {
            return source.captureStream(fps);
        }
        this.recordingCanvas = output;
        const copyFrame = () => {
            const sourceAspect = source.width / source.height;
            const outputAspect = width / height;
            let drawWidth = width;
            let drawHeight = height;
            let x = 0;
            let y = 0;
            if (sourceAspect > outputAspect) {
                drawHeight = width / sourceAspect;
                y = (height - drawHeight) / 2;
            }
            else {
                drawWidth = height * sourceAspect;
                x = (width - drawWidth) / 2;
            }
            context.fillStyle = getBackgroundCssColor(this.host);
            context.fillRect(0, 0, width, height);
            context.drawImage(source, x, y, drawWidth, drawHeight);
            this.recordingFrameId = requestAnimationFrame(copyFrame);
        };
        copyFrame();
        return output.captureStream(fps);
    }
    startRecording() {
        if (this.recorder) {
            return;
        }
        if (this.keyframes.length < 2) {
            this.host.showStatus('Video mode needs at least 2 keyframes to record');
            return;
        }
        if (typeof MediaRecorder === 'undefined') {
            this.host.showStatus('Recording is not supported in this environment');
            return;
        }
        // Prefer MP4/H.264 when the browser can mux it, fall back through WebM
        // codecs; never assume a specific one is available.
        const candidates = [
            'video/mp4;codecs=avc1',
            'video/mp4',
            'video/webm;codecs=vp9',
            'video/webm;codecs=vp8',
            'video/webm',
        ];
        const mime = candidates.find(m => MediaRecorder.isTypeSupported(m));
        if (!mime) {
            this.host.showStatus('No supported video codec found — cannot record');
            return;
        }
        // Recording during a preview restarts from the beginning: end the preview
        // (restoring the pre-preview camera, which play() below re-saves so it
        // still comes back after the recording) and record one full pass.
        if (this.playing) {
            this.stop();
        }
        // Bake the CSS canvas background into the scene for the recording (the
        // canvas itself is transparent — see savedBackground).
        this.savedBackground = this.host.scene.background;
        this.host.scene.background = new THREE.Color(getBackgroundCssColor(this.host));
        this.backgroundSwapped = true;
        this.host.requestRender();
        const stream = this.createRecordingStream();
        this.recordedChunks = [];
        this.recordedMime = mime;
        this.recorder = new MediaRecorder(stream, {
            mimeType: mime,
            videoBitsPerSecond: filmState.recordingBitrate,
        });
        this.recorder.ondataavailable = e => {
            if (e.data.size > 0) {
                this.recordedChunks.push(e.data);
            }
        };
        this.recorder.start(250);
        filmState.recording = true;
        this.host.showStatus(`Recording camera path (${mime.split(';')[0]})…`);
        this.play();
    }
    finishRecording() {
        const recorder = this.recorder;
        if (!recorder) {
            return;
        }
        this.recorder = null;
        filmState.recording = false;
        if (this.recordingFrameId !== null) {
            cancelAnimationFrame(this.recordingFrameId);
            this.recordingFrameId = null;
        }
        this.recordingCanvas = null;
        if (this.backgroundSwapped) {
            this.host.scene.background = this.savedBackground;
            this.savedBackground = null;
            this.backgroundSwapped = false;
            this.host.requestRender();
        }
        recorder.onstop = () => {
            const blob = new Blob(this.recordedChunks, { type: this.recordedMime });
            this.recordedChunks = [];
            recorder.stream.getTracks().forEach(track => track.stop());
            this.saveVideoBlob(blob);
        };
        recorder.stop();
    }
    saveVideoBlob(blob) {
        const ext = this.recordedMime.includes('mp4') ? 'mp4' : 'webm';
        const stamp = new Date().toISOString().replace(/[:T]/g, '-').replace(/\..+/, '');
        const fileName = `camera-path-${stamp}.${ext}`;
        if (isVSCode) {
            // Webviews can't trigger downloads; ship the bytes to the extension host.
            const reader = new FileReader();
            reader.onload = () => {
                const dataUrl = reader.result;
                this.host.vscode.postMessage({
                    type: 'saveVideo',
                    dataBase64: dataUrl.slice(dataUrl.indexOf(',') + 1),
                    defaultFileName: fileName,
                    extension: ext,
                });
            };
            reader.readAsDataURL(blob);
            return;
        }
        exportWithStatus(fileName, blob, message => this.host.showStatus(message));
    }
    // ------------------------------------------------------------- persistence
    buildProjectJson() {
        return JSON.stringify({ version: 1, type: 'camera-path', keyframes: this.keyframes }, null, 2);
    }
    saveProject() {
        if (this.keyframes.length === 0) {
            this.host.showStatus('No keyframes to save');
            return;
        }
        const stamp = new Date().toISOString().replace(/[:T]/g, '-').replace(/\..+/, '');
        const fileName = `camera-path-${stamp}.json`;
        const json = this.buildProjectJson();
        if (isVSCode) {
            this.host.vscode.postMessage({
                type: 'saveCameraPath',
                content: json,
                defaultFileName: fileName,
            });
            return;
        }
        exportWithStatus(fileName, new Blob([json], { type: 'application/json' }), message => this.host.showStatus(message));
    }
    loadProject(jsonText) {
        let parsed;
        try {
            parsed = JSON.parse(jsonText);
        }
        catch {
            this.host.showStatus('Camera path file is not valid JSON');
            return false;
        }
        const keyframes = parseKeyframeProject(parsed);
        if (!keyframes) {
            this.host.showStatus('Camera path file has no valid keyframes');
            return false;
        }
        this.stop();
        this.keyframes = keyframes;
        this.keyframeCounter = keyframes.length;
        this.syncState();
        this.host.showStatus(`Loaded camera path with ${keyframes.length} keyframes`);
        return true;
    }
    // ----------------------------------------------------------------- preview
    setFrustumsVisible(visible) {
        filmState.frustumsVisible = visible;
        this.rebuildFrustums();
        this.host.requestRender();
    }
    /**
     * Small CameraHelper per keyframe while editing. The helper cameras use a
     * short far plane (a fraction of the keyframe's distance to its target) so
     * the frustums read as markers instead of filling the scene.
     */
    rebuildFrustums() {
        if (this.frustumGroup) {
            this.host.scene.remove(this.frustumGroup);
            this.frustumGroup.traverse(obj => {
                const helper = obj;
                if (helper.geometry) {
                    helper.geometry.dispose();
                }
                if (helper.material?.dispose) {
                    helper.material.dispose();
                }
            });
            this.frustumGroup = null;
        }
        if (!filmState.frustumsVisible || this.keyframes.length === 0) {
            return;
        }
        this.frustumGroup = new THREE.Group();
        for (const key of this.keyframes) {
            const position = new THREE.Vector3().fromArray(key.position);
            const target = new THREE.Vector3().fromArray(key.target);
            const dist = Math.max(1e-3, position.distanceTo(target));
            const aspect = this.host.camera.aspect || 1;
            const cam = new THREE.PerspectiveCamera(key.fov, aspect, dist * 0.02, dist * 0.25);
            cam.position.copy(position);
            cam.quaternion.fromArray(key.quaternion);
            cam.updateMatrixWorld(true);
            this.frustumGroup.add(new THREE.CameraHelper(cam));
        }
        this.host.scene.add(this.frustumGroup);
    }
    toggleLoop() {
        filmState.loop = !filmState.loop;
        // The loop's closing segment changes the total duration.
        this.syncState();
    }
    /** Mirror manager state into the Svelte store the panel renders from. */
    syncState() {
        filmState.keyframes = this.keyframes.map(k => ({
            name: k.name,
            duration: k.duration,
            dwell: k.dwell,
            fov: k.fov,
        }));
        filmState.totalDuration =
            this.keyframes.length > 1 ? timelineDuration(this.keyframes, filmState.loop) : 0;
        if (filmState.frustumsVisible) {
            this.rebuildFrustums();
        }
        this.host.requestRender();
    }
    dispose() {
        this.stop();
        window.removeEventListener('pointerdown', this.onNavigationIntent, { capture: true });
        window.removeEventListener('wheel', this.onNavigationIntent, { capture: true });
        window.removeEventListener('touchstart', this.onNavigationIntent, { capture: true });
        filmState.frustumsVisible = false;
        this.rebuildFrustums();
    }
}
